/**
 * BasicsEditorModal — shared bottom-sheet modal that wraps
 * <AthleteBasicsEditor> so it can be opened from anywhere on the app
 * (the profile card's "Edit Profile" button + the Career panel). Any
 * change made inside pushes to the network via PUT /me/athlete/profile
 * so the public /network/<slug> page stays in sync in real time.
 */
import { useEffect } from 'react';
import { X, UserSquare } from 'lucide-react';
import { AthleteBasicsEditor } from '../../pages/AthleteMePage';

const BasicsEditorModal = ({ open, onClose, athlete, onSaved, onOpenRoleChange }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);
  if (!open) return null;

  const a = athlete || {};
  // iter454.16 · Only close when the click TARGET is the backdrop
  // itself. Without this guard, iOS Capacitor's WKWebView fires a
  // synthetic click at the last tap coordinates after the native file
  // picker closes — that synthetic click bubbles through the content
  // (even past `e.stopPropagation()` because iOS dispatches it on the
  // window, not the original target) and hits the overlay's onClick,
  // which was silently dismissing the editor mid-upload. Result: the
  // athlete tapped their photo, the picker opened, they picked a new
  // image, the modal disappeared before the upload could finish, and
  // it looked like "the whole screen just exits after 15 seconds."
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={handleBackdropClick}
      data-testid="basics-editor-modal"
      style={{
        // iter454.11 · Respect iOS safe areas + clear the sticky FLYT
        // header (top) and bottom tab bar so no field or the close
        // button gets sliced. `env(safe-area-inset-*)` picks up the
        // notch / home-indicator on native Capacitor; the +80px baseline
        // handles the always-visible chrome (header ~72px, tab bar ~72px).
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 88px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)',
        paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
      }}
    >
      {/* Wrapper is scrollable itself so tall forms don't cut off the
          header on short viewports (was clipped on iPad landscape). */}
      <div
        className="relative w-full max-w-2xl mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-lg hover:scale-105 transition"
          data-testid="basics-editor-modal-close"
          aria-label="Close editor"
        >
          <X className="w-4 h-4" />
        </button>
        <AthleteBasicsEditor
          alwaysExpanded
          fallbackPhoto={a.photo || ''}
          initial={{
            name:               a.name || '',
            network_hero_image: a.network_hero_image || '',
            network_tagline:    a.network_tagline || '',
            sport:              a.sport || '',
            team:               a.team || '',
            position:           a.position || '',
            hometown:           a.hometown || '',
            jersey_number:      a.jersey_number || '',
            grade_year:         a.grade_year || '',
            competition_level:  a.competition_level || '',
            business_email:     a.business_email || '',
          }}
          onSaved={onSaved}
        />
        {/* iter171 · Retired the inline PersonaPicker dropdown from the
            profile card because a single accidental tap glitched an
            agent → athlete and then stranded them (the picker was
            hidden for athletes). Persona changes now only happen from
            this modal via the pill below, which opens the full
            Edit Profile drawer with a 30-day lock confirm. */}
        {onOpenRoleChange && (
          <button
            type="button"
            onClick={onOpenRoleChange}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.10] ring-1 ring-white/10 hover:ring-white/25 text-sm font-bold text-white/80 hover:text-white transition"
            data-testid="basics-editor-open-role"
          >
            <UserSquare className="w-4 h-4" />
            Change my role · Athlete, Agent, Brand, Program, Fan
          </button>
        )}
      </div>
    </div>
  );
};

export default BasicsEditorModal;
